/**
 * Layer-2 (Zod fetch-boundary) STRUCTURAL GUARDS for the Email Marketing app.
 *
 * Response-shape parity campaign — Phase 6b Layer-2 adoption (structural-guard
 * posture). Plan:
 * agents/plans/response_shape_fleet_rollout_plan_2026-06-09.md
 *
 * These guards are passed to `useApiQuery`'s `schema:` option (via
 * {@link asResponseGuard}) so the unwrapped, post-transform payload is validated
 * at the network boundary. A failure throws an attributable
 * `ResponseValidationError` carrying the request URL instead of surfacing as a
 * `Cannot read properties of undefined` crash deep in a component render — the
 * exact silent-empty bug class from the 2026-04-29 CRM incident.
 *
 * POSTURE — DELIBERATELY LENIENT STRUCTURAL GUARD (NOT a full contracts schema):
 *   - No element / identity field is asserted (seo found backends omit declared
 *     `id`; email's envelopes carry `total` / `list_id` that vary by route).
 *   - `.passthrough()` on every object guard so a guard can NEVER drop a field a
 *     rendering page reads (hard rule #4 of the rollout plan).
 *   - `{ items }` envelopes only assert that, IF present, `items` is an array —
 *     catching the bare un-unwrapped object / null-body / wrong-type drift.
 *
 * These guards CANNOT over-reject a response the UI renders today. Upgrading any
 * to a faithful `@plerity/contracts` object schema MUST first capture the live
 * response and confirm `.parse()` succeeds (hard rule #1).
 *
 * The static `<T>` on each `useApiQuery` call stays owned by the hand-written
 * type in `../types/email` (Layer 1). {@link asResponseGuard} is the deliberate
 * seam casting these runtime guards to that static contract.
 */

import { z } from "zod";

/**
 * Cast a structural Zod guard to the hook's `ResponseValidator<T>` seam.
 *
 * Layer 1 (the hand-written `<T>`) owns the STATIC type; this guard owns RUNTIME
 * structure only. The lenient guard's inferred output intentionally differs from
 * `<T>`, so the cast bridges the two layers without weakening the static type.
 */
export function asResponseGuard<T>(schema: { parse(data: unknown): unknown }): {
  parse(data: unknown): T;
} {
  return schema as { parse(data: unknown): T };
}

/** Lenient object guard — asserts a non-null object; preserves every field. */
export const ObjectResponseGuard = z.object({}).passthrough();

/** Lenient array guard — asserts an array payload (catches un-unwrapped object / null body). */
export const ArrayResponseGuard = z.array(z.unknown());

/**
 * Lenient `{ items: [...] }` envelope guard.
 *
 * The email list endpoints tolerate BOTH the `{ items, total }` envelope AND a
 * bare top-level array — every consuming hook reads `query.data?.items`, so a
 * bare array degrades to an empty list rather than crashing (the
 * forms/segments/tags empty-state path returns `data: []`). The guard accepts
 * either container and preserves `total` / `list_id` / etc.; it rejects only a
 * null/primitive/error-string body — the gross drift this layer exists to catch.
 */
export const ItemsEnvelopeGuard = z.union([
  z.object({ items: z.array(z.unknown()).nullish() }).passthrough(),
  z.array(z.unknown()),
]);
